import { ObjectNumber } from '../ObjectNumber';
import { Type } from '../Type';
import {
  DocumentType,
  DocumentTypes,
  DraftInterface
} from '../../Interfaces/Draft/DraftInterface';
import { LineInterface } from '../../Interfaces/Draft/LineInterface';
import { DraftFieldsInterface } from '../../Interfaces/Draft/FieldsInterface';

const UNIT_PRICE_DECIMAL_PLACES = 10;

/**
 *
 *
 * @export
 * @param {DraftInterface} payload
 * @returns
 */
export function calculateValuesForDraft(payload: DraftInterface) {
  const lines = payload.lines;
  let grossTotal = new ObjectNumber();
  let totalTax = new ObjectNumber();
  let netTotal = new ObjectNumber();
  let totalDiscount = new ObjectNumber();
  const totalWork = new ObjectNumber();

  const vatMap = {};
  const pushVAT = (vat: number, amount: number) => {
    if (!vatMap[vat]) {
      vatMap[vat] = { amount: ObjectNumber.fromNumber(0), percentage: vat };
    }

    vatMap[vat].amount.add(amount);
  };

  for (const line of lines) {
    if (Type.isInvoiceLine(line)) {
      totalTax.add(line.taxTotal);
      grossTotal.add(line.grossTotal);
      netTotal.add(line.netTotal);
      pushVAT(line.vatPct, line.taxTotal);
    }
  }

  const vatArray = [];
  for (const i in vatMap) {
    vatMap[i].amount = vatMap[i].amount.toNumber();
    vatArray.push(vatMap[i]);
  }

  const ratio = grossTotal.copy().divide(netTotal);

  if (
    payload.fields.discountEnabled &&
    payload.fields.discountEnabled.value === true &&
    payload.fields.discountAmount &&
    payload.fields.discountAmount.value > 0 &&
    payload.fields.discountLocation &&
    payload.fields.discountLocation.value === "perTotal"
  ) {
    if (payload.fields.discountType.value === "amount") {
      totalDiscount.add(payload.fields.discountAmount.value);
      let originalTotalTax, vatRatio;
      originalTotalTax = totalTax.copy();
      if (payload.fields.discountVAT.value === 'preVAT') {
        payload.document === DocumentTypes.CREDITNOTE
          ? netTotal.add(totalDiscount)
          : netTotal.subtract(totalDiscount);
        grossTotal = netTotal.copy().multiply(ratio);
        totalTax = grossTotal.copy().subtract(netTotal);
        // totalDiscount = grossTotal.copy().subtract(netTotal);
      } else {
        payload.document === DocumentTypes.CREDITNOTE
          ? grossTotal.add(totalDiscount)
          : grossTotal.subtract(totalDiscount);
        netTotal = grossTotal.copy().divide(ratio);
        totalTax = grossTotal.copy().subtract(netTotal);
      }
      // update vat array to match discounted values
      vatArray.forEach(v => {
        vatRatio =
          originalTotalTax.toNumber() !== 0
            ? v.amount / originalTotalTax.toNumber()
            : 0;
        v.amount = new ObjectNumber(
          totalTax
            .copy()
            .multiply(vatRatio)
            .toNumber()
        ).toNumber(2);
      });
    } else {
      // percentage
      totalDiscount = grossTotal
        .copy()
        .divide(100)
        .multiply(payload.fields.discountAmount.value);
      grossTotal.subtract(totalDiscount);
      netTotal = grossTotal.copy().divide(ratio);
      totalTax = grossTotal.copy().subtract(netTotal);
      // update vat array to match discounted values
      vatArray.forEach((v) => {
        v.amount = ObjectNumber.fromNumber(v.amount)
          .multiply(
            ObjectNumber.fromNumber(1).subtract(
              ObjectNumber.fromNumber(
                payload.fields.discountAmount.value
              ).divide(100)
            )
          )
          .toNumber();
      });
    }
  }

  payload.summary.taxSummary = vatArray.sort((a, b) => {
    return a.percentage - b.percentage;
  });
  payload.summary.workTotal = totalWork.toNumber();
  if (payload.fields.deduction) {
    payload.summary.reductionTotal = totalWork.copy().multiply((payload.fields.deduction.value || 0) / 100).toNumber();
  }
  payload.summary.discountTotal = totalDiscount.toNumber();
  payload.summary.grossTotal = grossTotal.toNumber();
  payload.summary.taxTotal = totalTax.toNumber();
  payload.summary.netTotal = netTotal.toNumber();
  return payload;
}

const applyDiscount = (total, discount, document) => {
  document === DocumentTypes.CREDITNOTE
    ? total.add(discount)
    : total.subtract(discount);
};

/**
 *
 * @param line
 * @param fields
 * @param document
 */
const calculateLineValues = (
  line: LineInterface,
  fields: DraftFieldsInterface,
  document?: DocumentType
) => {
  let one, vat, unitPrice, total, discount, discountAmount, lineTotalPreVAT;
  one = new ObjectNumber(1);
  unitPrice = new ObjectNumber(line.unitPrice);
  lineTotalPreVAT = ObjectNumber.fromNumber(line.quantity).multiply(unitPrice);
  return {
    when: {
      hasDiscountAmount: {
        postVAT: {
          defFieldIsPrice: () => {
            // grossTotal = ROUND((unitPrice x quantity x ( 1 + VAT%)) - discount)
            vat = lineTotalPreVAT.copy().multiply(line.vatPct);
            total = lineTotalPreVAT.copy().add(vat);
            applyDiscount(total, discount, document);
            vat = total.copy().subtract(total.copy().divide(1 + line.vatPct));
            return { total, vat, unitPrice, discountAmount };
          },
          defFieldIsTotal: () => {
            // grossTotal = ( ROUND((unitPrice x ( 1 + VAT%))) - (discount / quantity) ) x quantity
            vat = lineTotalPreVAT.copy().multiply(line.vatPct);
            const unitPriceWithVAT = unitPrice.copy().multiply(1 + line.vatPct);
            total = new ObjectNumber(unitPriceWithVAT.toNumber()).multiply(
              line.quantity
            );
            applyDiscount(total, discount, document);
            vat = total.copy().subtract(total.copy().divide(1 + line.vatPct));
            return { total, vat, unitPrice, discountAmount };
          }
        },
        preVAT: () => {
          // grossTotal = ROUND((unitPrice x quantity - discount) x ( 1 + VAT%))
          const tempTotal = lineTotalPreVAT.copy();
          document === DocumentTypes.CREDITNOTE
            ? tempTotal.add(discountAmount)
            : tempTotal.subtract(discountAmount);
          vat = tempTotal.copy().multiply(line.vatPct);
          total = tempTotal.copy().add(vat);
          return { total, vat, unitPrice, discountAmount };
        }
      },
      hasDiscountPercent: {
        defFieldIsPrice: () => {
          // grossTotal = ROUND(unitPrice x quantity x ( 1 + VAT%) x (1 - discountPct))
          vat = lineTotalPreVAT
            .copy()
            .multiply(one.copy().subtract(discount.copy().divide(100)))
            .multiply(line.vatPct);
          total = lineTotalPreVAT
            .copy()
            .multiply(one.copy().subtract(discount.copy().divide(100)))
            .multiply(1 + line.vatPct);
          discountAmount = lineTotalPreVAT
            .copy()
            .multiply(discount.copy().divide(100))
            .multiply(1 + line.vatPct);
          return { total, vat, unitPrice, discountAmount };
        },
        defFieldIsTotal: () => {
          // grossTotal = ROUND(unitPrice x ( 1 + VAT%)) x quantity x (1 - discountPct))
          vat = lineTotalPreVAT
            .copy()
            .multiply(one.copy().subtract(discount.copy().divide(100)))
            .multiply(line.vatPct);
          const unitTotal = unitPrice.copy().multiply(1 + line.vatPct);
          total = new ObjectNumber(unitTotal.toNumber())
            .multiply(line.quantity)
            .multiply(one.copy().subtract(discount.copy().divide(100)));
          discountAmount = lineTotalPreVAT
            .copy()
            .multiply(discount.copy().divide(100))
            .multiply(1 + line.vatPct);
          return { total, vat, unitPrice, discountAmount };
        }
      },
      doesNotHaveDiscountPerLine: {
        defFieldIsPrice: () => {
          // grossTotal = ROUND(unitPrice x quantity x ( 1 + VAT%))
          vat = lineTotalPreVAT.copy().multiply(line.vatPct);
          total = lineTotalPreVAT.copy().add(vat);
          return { total, vat, unitPrice, discountAmount };
        },
        defFieldIsTotal: () => {
          // grossTotal = ROUND(unitPrice x ( 1 + VAT%)) x quantity
          vat = lineTotalPreVAT.copy().multiply(line.vatPct);
          const unitPriceWithVAT = unitPrice.copy().multiply(1 + line.vatPct);
          total = new ObjectNumber(unitPriceWithVAT.toNumber()).multiply(
            line.quantity
          );
          return { total, vat, unitPrice, discountAmount };
        }
      }
    }
  };
};

/**
 *
 * @param line
 * @param fields
 * @param document
 */
const calculateLineValuesForDefFieldNotGrossTotal = (
  line: LineInterface,
  fields: DraftFieldsInterface,
  document?: DocumentType
) => {
  // no discount at all or discount per draft
  return calculateLineValues(
    line,
    fields,
    document
  ).when.doesNotHaveDiscountPerLine.defFieldIsPrice();
};

/**
 *
 *
 * @export
 * @param {LineInterface} line
 * @param {DraftFieldsInterface} fields
 * @param {String} [document]
 */
export function calculateValuesForLine(
  line: LineInterface,
  fields: DraftFieldsInterface,
  document?: DocumentType
) {
  let lineValues;
  lineValues = calculateLineValuesForDefFieldNotGrossTotal(
    line,
    fields,
    document
  );

  line.netTotal = ObjectNumber.fromObjectNumber(lineValues.total)
    .subtract(lineValues.vat)
    .toNumber();
  line.taxTotal = lineValues.vat.toNumber();
  line.unitPrice = lineValues.unitPrice.toNumber(UNIT_PRICE_DECIMAL_PLACES);
  line.grossTotal = lineValues.total.toNumber();
}
